import axios from 'axios';
import { contractorToken } from './contractorAuth';
import { storedViewAsContractorId } from './viewAs';
import { searchSettings } from '../pages/settings/registry';

/**
 * The global search (Ctrl+K, the box in the top bar): the calls, and the
 * recent searches.
 *
 * WHO IS ASKING, the way lib/docs.js says it: a member of staff through the
 * interceptor (lib/apiClient - View as rides along in its header, so the
 * server answers as the person being viewed), a contractor by their portal
 * token, and an administrator looking through a contractor's portal names
 * them with as=c:<id>. The server decides everything (backend
 * lib/search-sections.js); this only asks.
 *
 * RECENT SEARCHES are this browser's alone - nothing about what anybody
 * searched for is kept on the server - and kept per person: under the key of
 * whoever the box is searching as, so an administrator using View as does
 * not see the viewed person's, nor they the administrator's.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const viewingContractor = () => (!contractorToken() && storedViewAsContractorId()) || null;

/** The group order, and what each is called - the server's sections, plus Settings (answered here). */
export const SECTION_LABELS = {
  tickets: 'Tickets', assets: 'Assets', people: 'People', help: 'Help', catalogue: 'Request something',
  docs: 'Docs', links: 'Links', todos: 'To-dos', chat: 'Chat', licences: 'Licences', contracts: 'Contracts',
  settings: 'Settings'
};

/**
 * Ask. `opts`: { sections: [keys], limit, wait: 'long', signal }. Answers the
 * server's { q, terms, sections, indexed? }, with Settings filled in here.
 */
export async function searchAll(q, { sections = null, limit = null, wait = null, signal } = {}) {
  const token = contractorToken();
  const params = { q };
  if (sections?.length) params.sections = sections.join(',');
  if (limit) params.limit = limit;
  if (wait) params.wait = wait;
  const as = viewingContractor();
  if (as) params.as = `c:${as}`;
  const r = await axios.get(`${API}/search`, {
    params,
    signal,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  const data = r.data || { sections: [] };
  return {
    ...data,
    sections: (data.sections || []).map((s) => (s.client && s.key === 'settings' ? settingsSection(q, s, limit) : s))
  };
}

/** Settings, from the registry the Settings page searches (pages/settings/registry.js). */
function settingsSection(q, s, limit) {
  const found = searchSettings(q, null);
  const n = limit || 5;
  return {
    ...s,
    status: 'ok',
    more: found.length > n,
    items: found.slice(0, n).map((r) => ({
      type: 'setting',
      id: r.key,
      title: r.label,
      title_highlights: marks(r.label, q),
      subtitle: r.where,
      snippet: null,
      highlights: [],
      link: r.path,
      ...(r.external ? { external: r.path } : {})
    }))
  };
}

/** Where `q`'s words are in `text`, as ranges - the same rule as the server's. */
export function marks(text, q) {
  const words = (String(q || '').toLowerCase().match(/[\p{L}\p{N}]+/gu) || []).filter((w, i, all) => all.length === 1 || w.length > 1);
  const s = String(text || '');
  const low = s.toLowerCase();
  const found = [];
  for (const w of words) {
    let at = low.indexOf(w);
    while (at >= 0) { found.push([at, at + w.length]); at = low.indexOf(w, at + Math.max(1, w.length)); }
  }
  found.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged = [];
  for (const r of found) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([...r]);
  }
  return merged;
}

/**
 * Text with its ranges, as pieces for React to draw: [{ text, hit }]. React
 * puts each in a text node, so nothing anybody typed is ever parsed as HTML.
 */
export function pieces(text, ranges) {
  const s = String(text || '');
  const out = [];
  let at = 0;
  for (const [a, b] of ranges || []) {
    if (a < at || b > s.length || a >= b) continue;
    if (a > at) out.push({ text: s.slice(at, a), hit: false });
    out.push({ text: s.slice(a, b), hit: true });
    at = b;
  }
  if (at < s.length) out.push({ text: s.slice(at), hit: false });
  return out;
}

/* ------------------------------------------------------------ recent searches */

const RECENT_MAX = 8;
const recentKey = (who) => `jarvis.search.recent.${who || 'nobody'}`;

/** This person's recent searches, newest first. Empty when storage is not available. */
export function recentSearches(who) {
  try {
    const raw = JSON.parse(localStorage.getItem(recentKey(who)) || '[]');
    return Array.isArray(raw) ? raw.filter((x) => typeof x === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

export function rememberSearch(who, q) {
  const t = String(q || '').trim().slice(0, 120);
  if (!t) return;
  try {
    const list = [t, ...recentSearches(who).filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, RECENT_MAX);
    localStorage.setItem(recentKey(who), JSON.stringify(list));
  } catch { /* a private window: nothing remembered */ }
}

export function forgetSearches(who) {
  try { localStorage.removeItem(recentKey(who)); } catch { /* nothing to forget */ }
}
